import { useMemo, useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { Check, Loader2, ArrowRight, X, Building2 } from 'lucide-react';
import { Button, ClubIcon } from '@/components/ui';
import { SubpageHeader } from '@/components/ui/SubpageHeader';
import { cn } from '@/lib/utils';
import { BenefitRedeemModal } from './BenefitRedeemModal';
import { useModal } from '@/hooks';
import { useGetClubsAvailable, useGetMyClub } from '@/services/queries/club';
import { useGetMyCompany } from '@/services/queries/company';
import { useJoinClub } from '@/services/mutations/club';
import { buildCheckoutRedirectUrl } from '@/utils/checkoutRedirectOrigin';
import { processClubBenefitsForUser } from '@/utils/clubMonthlySummary';
import type { ClubProcessedBenefit } from '@/utils/clubMonthlySummary';
import { ClubMonthlySummaryCard } from './ClubMonthlySummaryCard';
import { ClubConsumableBenefitsSection } from './ClubConsumableBenefitsSection';
import { buildClubBenefitConsultaPath } from './clubBenefitActions';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { toast } from 'react-toastify';

const sortBenefitsByPriority = (benefits?: any[]) =>
	[...(benefits ?? [])].sort(
		(a, b) => (b?.display_order ?? 0) - (a?.display_order ?? 0),
	);

function capitalizeFirst(str: string): string {
	if (!str || typeof str !== 'string') return str;
	const i = str.search(/[a-zA-ZÀ-ÿ]/);
	if (i === -1) return str;
	return str.slice(0, i) + str[i].toUpperCase() + str.slice(i + 1);
}

/** Qual card destacar com “pagamento pendente” sem mudança no backend (ex.: após “Assinar agora”). */
const PENDING_CHECKOUT_CLUB_STORAGE_KEY = 'plumaa_pending_checkout_club_id';

/** Sessão de checkout Stripe expira 24h após criação (padrão); `created_at` da assinatura PENDING costuma coincidir. */
const PENDING_CHECKOUT_TTL_MS = 24 * 60 * 60 * 1000;

function usePendingCheckoutMinutesRemaining(createdAtIso: string | undefined) {
	const [minutes, setMinutes] = useState<number | null>(() => {
		if (!createdAtIso) return null;
		const end = new Date(createdAtIso).getTime() + PENDING_CHECKOUT_TTL_MS;
		const msLeft = end - Date.now();
		if (msLeft <= 0) return 0;
		return Math.ceil(msLeft / 60_000);
	});

	useEffect(() => {
		if (!createdAtIso) {
			setMinutes(null);
			return;
		}
		const tick = () => {
			const end =
				new Date(createdAtIso).getTime() + PENDING_CHECKOUT_TTL_MS;
			const msLeft = end - Date.now();
			setMinutes(msLeft <= 0 ? 0 : Math.ceil(msLeft / 60_000));
		};
		tick();
		const id = window.setInterval(tick, 30_000);
		return () => window.clearInterval(id);
	}, [createdAtIso]);

	return minutes;
}

const PENDING_CHECKOUT_EXPIRY_FALLBACK =
	'Quando o prazo do link de pagamento expirar, seu pagamento pendente será cancelado automaticamente.';

const getPlansGridColsClass = (count: number) => {
	if (count <= 1) return 'lg:grid-cols-1';
	if (count === 2) return 'lg:grid-cols-2';
	if (count === 3) return 'lg:grid-cols-3';
	return 'lg:grid-cols-4';
};

function pendingCheckoutExpiryLabel(
	minutes: number | null,
	hasCreatedAt: boolean,
): string {
	if (!hasCreatedAt) return PENDING_CHECKOUT_EXPIRY_FALLBACK;
	if (minutes === null) return PENDING_CHECKOUT_EXPIRY_FALLBACK;
	if (minutes <= 0) {
		return 'O prazo deste checkout pode ter expirado. Se o link não funcionar, atualize a página ou inicie uma nova assinatura.';
	}
	if (minutes === 1) {
		return 'Em 1 minuto seu pagamento pendente será automaticamente cancelado.';
	}
	return `Em ${minutes} minutos seu pagamento pendente será automaticamente cancelado.`;
}

type PendingCheckoutScreenProps = {
	checkoutUrl: string;
	pendingCheckoutCreatedAt?: string;
	titleText: string;
};

type JoinClubConfirmModalProps = {
	isOpen: boolean;
	clubName?: string;
	isPending: boolean;
	onClose: () => void;
	onConfirm: () => void;
};

function JoinClubConfirmModal({
	isOpen,
	clubName,
	isPending,
	onClose,
	onConfirm,
}: JoinClubConfirmModalProps) {
	if (!isOpen) return null;

	return createPortal(
		<div className="fixed inset-0 z-[9999] flex items-center justify-center p-4">
			<div
				className="absolute inset-0 bg-stone-900/60 backdrop-blur-[2px]"
				onClick={isPending ? undefined : onClose}
			/>
			<div className="animate-in zoom-in-95 relative flex max-h-[90vh] w-full max-w-[520px] flex-col overflow-hidden rounded-[2rem] bg-white shadow-2xl duration-200">
				<div className="flex shrink-0 items-center justify-between border-b border-stone-50 px-8 py-6">
					<h2 className="text-lg font-black text-stone-900">
						Confirmar assinatura
					</h2>
					<button
						type="button"
						onClick={onClose}
						disabled={isPending}
						className="text-stone-400 transition-colors hover:text-stone-600 disabled:opacity-50"
					>
						<X size={20} />
					</button>
				</div>
				<div className="flex-1 space-y-4 overflow-y-auto p-8 text-sm text-stone-600">
					<p className="font-semibold text-stone-900">
						Você está prestes a assinar o clube {clubName}.
					</p>
					<p>
						Ao continuar, você será redirecionado para um ambiente
						seguro de pagamento para concluir sua assinatura.
					</p>
					<p className="rounded-xl border border-stone-100 bg-stone-50 px-4 py-3 text-xs leading-relaxed text-stone-500">
						Lembrete: os créditos do clube valem apenas dentro do
						mês da assinatura, não acumulam para o mês seguinte e
						não são convertidos em dinheiro. Se ficou com alguma
						dúvida, vale reler a{' '}
						<a
							href="/termos-de-uso"
							target="_blank"
							rel="noopener noreferrer"
							className="font-semibold text-stone-700 underline underline-offset-2 hover:text-stone-900"
						>
							Cláusula 8 dos Termos de Uso
						</a>
						.
					</p>
				</div>
				<div className="flex shrink-0 items-center justify-end gap-6 border-t border-stone-50 p-8">
					<button
						type="button"
						onClick={onClose}
						disabled={isPending}
						className="text-sm font-bold text-stone-400 hover:text-stone-600 disabled:opacity-50"
					>
						Voltar
					</button>
					<Button
						type="button"
						text={isPending ? 'Processando...' : 'Assinar'}
						disabled={isPending}
						onClick={onConfirm}
						className="rounded-full bg-black px-6 py-2.5 text-sm font-bold text-white shadow-lg hover:bg-neutral-800"
						startIcon={
							isPending ? (
								<Loader2 className="size-4 animate-spin" />
							) : (
								<Check className="size-4 stroke-[3]" />
							)
						}
					/>
				</div>
			</div>
		</div>,
		document.body,
	);
}

function PendingCheckoutScreen({
	checkoutUrl,
	pendingCheckoutCreatedAt,
	titleText,
}: PendingCheckoutScreenProps) {
	const minutesLeft = usePendingCheckoutMinutesRemaining(
		pendingCheckoutCreatedAt,
	);
	const expiryLabel = pendingCheckoutExpiryLabel(
		minutesLeft,
		Boolean(pendingCheckoutCreatedAt),
	);

	return (
		<div className="mx-auto w-full max-w-xl space-y-8 text-center">
			<div className="space-y-4">
				<p className="text-[11px] font-black uppercase tracking-[0.25em] text-brand-orange">
					Pagamento pendente
				</p>
				<h1 className="text-3xl font-bold tracking-tight text-zinc-900 md:text-4xl">
					{titleText}
				</h1>
				<p className="text-sm font-semibold leading-relaxed text-stone-800 md:text-base">
					{expiryLabel}
				</p>
				<p className="text-base font-medium leading-relaxed text-zinc-600 md:text-lg">
					Você iniciou a assinatura, mas o pagamento ainda não foi
					concluído. Para liberar seu acesso ao clube, continue no
					ambiente seguro de pagamento. Se preferir, copie o link e
					finalize em outro momento ou em outro dispositivo.
				</p>
			</div>
			<div className="space-y-3 pt-2">
				<Button
					className="hover:bg-brand-orange/92 w-full justify-center rounded-2xl border-2 border-brand-orange bg-brand-orange px-4 py-4 text-[11px] font-black uppercase tracking-[0.18em] text-white shadow-sm transition-all"
					text="Continuar o pagamento"
					onClick={() => {
						window.location.href = checkoutUrl;
					}}
				/>
				<Button
					variant="outline"
					className="w-full justify-center rounded-2xl border-2 border-brand-orange/35 bg-white px-4 py-3 text-[11px] font-black uppercase tracking-[0.18em] text-brand-orange hover:bg-brand-orange/5"
					text="Copiar link de pagamento"
					onClick={async () => {
						try {
							await navigator.clipboard.writeText(checkoutUrl);
							toast.success('Link copiado!');
						} catch {
							toast.error('Não foi possível copiar.');
						}
					}}
				/>
			</div>
		</div>
	);
}

type ClubsPageShellProps = {
	breadcrumbSuffix: string;
	onBack: () => void;
	children: React.ReactNode;
};

function ClubsPageShell({
	breadcrumbSuffix,
	onBack,
	children,
}: ClubsPageShellProps) {
	return (
		<div
			data-site-tutorial="clubs-root"
			className="flex min-h-full w-full items-start justify-center bg-[#f8f9fa] px-3 py-4 pb-32 md:px-4 md:py-5 md:pb-40"
		>
			<div className="animate-in fade-in w-full max-w-[1280px] space-y-6 duration-500">
				<SubpageHeader
					title="Meus Clubes"
					breadcrumb={['CLUBES', breadcrumbSuffix]}
					onBack={onBack}
				/>
				{children}
			</div>
		</div>
	);
}

const UserClubs = () => {
	const navigate = useNavigate();
	const [searchParams, setSearchParams] = useSearchParams();
	const { openModal } = useModal();

	const {
		data: myClub,
		isLoading: isLoadingMyClub,
		refetch: refetchMyClub,
	} = useGetMyClub();

	const { data: myCompany, isLoading: isLoadingMyCompany } =
		useGetMyCompany();

	const isInCompany = !!myCompany;
	const hasCompanyClubs = (myCompany?.company_club?.length ?? 0) > 0;
	const activeCompanyPlan = (myCompany?.company_plan_subscription ?? []).find(
		(sub) => sub.status === 'ACTIVE',
	);
	const hasActiveCompanyPlan = !!activeCompanyPlan;

	// Funcionário, empresa tem clubes configurados, mas ainda não ativou o benefício (sem plano ativo)
	const shouldDisableJoinForEmployee =
		isInCompany && hasCompanyClubs && !hasActiveCompanyPlan;

	// Lista "available" já vem resolvida no backend (B2B2C se employee, senão B2C)
	const { data: clubsAvailableData, isLoading: isLoadingClubsAvailable } =
		useGetClubsAvailable({
			enabled:
				!isLoadingMyClub &&
				!myClub?.subscription &&
				!isLoadingMyCompany,
		});

	const { mutate: joinClub, isPending: isJoining } = useJoinClub(
		(checkoutUrl) => {
			window.location.href = checkoutUrl;
		},
	);

	const handleOpenPortal = () => {
		if (myClub?.portalUrl) {
			window.location.href = myClub.portalUrl;
		} else {
			toast.error('Link do portal não disponível.');
		}
	};

	const onBack = () => navigate('/home');

	useEffect(() => {
		const success = searchParams.get('success');
		const canceled = searchParams.get('canceled');

		if (success === 'true') {
			toast.success('Pagamento identificado! Atualizando status...');
			searchParams.delete('success');
			setSearchParams(searchParams);
			refetchMyClub();
		}

		if (canceled === 'true') {
			toast.info('O processo de pagamento foi cancelado.');
			searchParams.delete('canceled');
			setSearchParams(searchParams);
		}
	}, [searchParams, setSearchParams, refetchMyClub]);

	const handleJoinClub = (clubId: string) => {
		sessionStorage.setItem(PENDING_CHECKOUT_CLUB_STORAGE_KEY, clubId);
		setPendingCheckoutClubId(clubId);
		const successUrl = buildCheckoutRedirectUrl('/clubes');
		const cancelUrl = buildCheckoutRedirectUrl('/clubes');

		joinClub({
			clubId,
			successUrl,
			cancelUrl,
		});
	};

	const handleOpenJoinClubModal = (club: any) => {
		setJoinClubToConfirm(club);
	};

	const handleUseBenefit = (benefit: ClubProcessedBenefit) => {
		navigate(buildClubBenefitConsultaPath(benefit));
	};

	const sortedAvailableClubs = useMemo(() => {
		const clubs = Array.isArray(clubsAvailableData)
			? clubsAvailableData
			: [];
		return [...clubs].sort(
			(a, b) => (b.render_order ?? 0) - (a.render_order ?? 0),
		);
	}, [clubsAvailableData]);

	const pickClubsForDisplay = (clubs: any[]) => {
		const sorted = [...clubs].sort(
			(a, b) => (b.render_order ?? 0) - (a.render_order ?? 0),
		);
		const highlighted = sorted.find((c) => c.render_type === 'HIGHLIGHTED');
		const top = sorted.slice(0, 4);
		if (!highlighted) return top;
		if (top.some((c) => c.id === highlighted.id)) return top;
		if (top.length < 4) return [...top, highlighted];
		return [...top.slice(0, 3), highlighted];
	};

	const companyClubs = useMemo(
		() => (isInCompany ? pickClubsForDisplay(sortedAvailableClubs) : []),
		[isInCompany, sortedAvailableClubs],
	);

	const sortedB2CClubs = useMemo(
		() => (!isInCompany ? pickClubsForDisplay(sortedAvailableClubs) : []),
		[isInCompany, sortedAvailableClubs],
	);

	const [pendingCheckoutClubId, setPendingCheckoutClubId] = useState<
		string | null
	>(() =>
		typeof window !== 'undefined'
			? sessionStorage.getItem(PENDING_CHECKOUT_CLUB_STORAGE_KEY)
			: null,
	);
	const [joinClubToConfirm, setJoinClubToConfirm] = useState<any | null>(
		null,
	);

	useEffect(() => {
		if (myClub?.subscription?.status === 'ACTIVE' || !myClub?.checkoutUrl) {
			sessionStorage.removeItem(PENDING_CHECKOUT_CLUB_STORAGE_KEY);
			setPendingCheckoutClubId(null);
		}
	}, [myClub?.checkoutUrl, myClub?.subscription?.status]);

	const allBenefitsList = useMemo(() => {
		const subscription = myClub?.subscription;
		const club = subscription?.club;
		const benefits = sortBenefitsByPriority(club?.benefits ?? []);
		const userBenefits = subscription?.userClubSubscriptionBenefits ?? [];
		return benefits.map((benefit: any) => {
			const userBenefitUsage = userBenefits.find(
				(b: any) => b.club_benefit_id === benefit.id,
			);
			const isInfo = benefit.service?.type === 'INFORMATION';
			const title =
				benefit.service_substitute_title ||
				benefit.service?.service_name ||
				'';
			const description = benefit.service?.service_name || '';
			return {
				id: benefit.id,
				title,
				description,
				isInfo,
				used: userBenefitUsage?.used_count ?? 0,
				total: benefit.default_amount ?? 0,
				renewal_trigger: benefit.renewal_trigger,
			};
		});
	}, [myClub]);

	if (isLoadingMyClub) {
		return (
			<ClubsPageShell breadcrumbSuffix="PLANOS" onBack={onBack}>
				<div className="flex justify-center py-20">
					<Loader2 className="h-10 w-10 animate-spin text-stone-300" />
				</div>
			</ClubsPageShell>
		);
	}

	// --- VIEW: VITRINE DE CLUBES ---
	if (!myClub?.subscription) {
		if (myClub?.checkoutUrl) {
			const pendingClubName = pendingCheckoutClubId
				? sortedAvailableClubs.find(
						(c) => c.id === pendingCheckoutClubId,
				  )?.name
				: null;
			const titleText = pendingClubName || 'Conclua seu pagamento';
			return (
				<ClubsPageShell breadcrumbSuffix="PAGAMENTO" onBack={onBack}>
					<PendingCheckoutScreen
						checkoutUrl={myClub.checkoutUrl}
						pendingCheckoutCreatedAt={
							myClub.pendingCheckoutCreatedAt
						}
						titleText={titleText}
					/>
				</ClubsPageShell>
			);
		}

		const renderClubCard = (
			club: any,
			_index: number,
			isHighlighted: boolean,
		) => {
			const rawPrice = parseFloat(club.price || '0');
			const subsidyPerEmployee = Number(
				activeCompanyPlan?.company_plan?.subsidy_per_employee ?? 0,
			);
			const hasSubsidy = subsidyPerEmployee > 0 && rawPrice > 0;
			const effectiveSubsidy = hasSubsidy
				? Math.min(subsidyPerEmployee, rawPrice)
				: 0;
			const subsidizedPrice = Math.max(0, rawPrice - effectiveSubsidy);

			const formatPrice = (value: number) =>
				value.toLocaleString('pt-BR', {
					minimumFractionDigits: 2,
					maximumFractionDigits: 2,
				});

			return (
				<div
					key={club.id}
					className={cn(
						'relative flex flex-col justify-between gap-6 rounded-[2.5rem] p-7 transition-all duration-300 lg:gap-5 lg:p-6',
						isHighlighted
							? 'z-10 bg-zinc-950 text-white shadow-2xl md:scale-105'
							: 'border border-zinc-200 bg-white shadow-sm hover:shadow-2xl hover:shadow-zinc-200/60',
					)}
				>
					{isHighlighted && (
						<div className="absolute -top-4 right-8 rounded-full border border-zinc-800 bg-zinc-900 px-4 py-2 text-[10px] font-black uppercase tracking-widest text-white">
							Recomendado
						</div>
					)}

					<div className="space-y-6 text-left">
						<div className="flex items-center gap-4">
							<ClubIcon
								club={club}
								size="lg"
								className={cn(
									isHighlighted &&
										'border border-zinc-800 bg-zinc-900',
								)}
							/>
							<div className="space-y-0.5">
								<h3
									className={cn(
										'text-2xl font-bold leading-tight',
										isHighlighted
											? 'text-white'
											: 'text-zinc-900',
									)}
								>
									{club.name}
								</h3>
								<p className="text-[10px] font-black uppercase tracking-widest text-brand-orange">
									{hasSubsidy ? (
										<>
											<span className="mr-1 line-through opacity-70">
												{club.currency === 'BRL'
													? 'R$'
													: club.currency}{' '}
												{formatPrice(rawPrice)}
											</span>
											<span>
												{club.currency === 'BRL'
													? 'R$'
													: club.currency}{' '}
												{formatPrice(subsidizedPrice)}{' '}
												/mês
											</span>
										</>
									) : (
										<>
											{club.currency === 'BRL'
												? 'R$'
												: club.currency}{' '}
											{formatPrice(rawPrice)} /mês
										</>
									)}
								</p>
							</div>
						</div>

						{club.slogan && (
							<p
								className={cn(
									'text-sm font-medium',
									isHighlighted
										? 'text-zinc-400'
										: 'text-zinc-500',
								)}
							>
								{club.slogan}
							</p>
						)}

						<ul className="space-y-4">
							{sortBenefitsByPriority(club.benefits).map(
								(benefit: any) => (
									<li
										key={benefit.id}
										className="flex items-start gap-3 text-sm font-medium"
									>
										<Check
											size={18}
											className={cn(
												'mt-0.5 shrink-0 text-brand-orange',
											)}
										/>
										<span
											className={
												isHighlighted
													? 'text-zinc-400'
													: 'text-zinc-500'
											}
										>
											{benefit.service_substitute_title ||
												benefit.service?.service_name}
										</span>
									</li>
								),
							)}
						</ul>
					</div>

					<Button
						className={cn(
							'w-full justify-center rounded-2xl border-none px-4 py-4 text-[11px] font-black uppercase tracking-[0.18em] transition-all',
							isHighlighted
								? 'hover:bg-brand-orange/92 bg-brand-orange text-white shadow-[0_0_20px_rgba(255,122,86,0.35)]'
								: 'hover:bg-brand-orange/92 border-2 border-brand-orange bg-brand-orange text-white shadow-sm',
							shouldDisableJoinForEmployee &&
								(isHighlighted
									? 'bg-brand-orange/60 text-white hover:bg-brand-orange/60'
									: 'border-brand-orange/40 bg-brand-orange/60 text-white hover:bg-brand-orange/60'),
							isJoining && 'cursor-wait opacity-70',
							shouldDisableJoinForEmployee &&
								'cursor-not-allowed',
						)}
						text={
							isJoining
								? 'Processando...'
								: shouldDisableJoinForEmployee
								  ? 'Indisponível pela empresa'
								  : 'Assinar agora'
						}
						disabled={isJoining || shouldDisableJoinForEmployee}
						startIcon={
							isJoining ? (
								<Loader2 className="animate-spin" size={16} />
							) : undefined
						}
						onClick={() => {
							handleOpenJoinClubModal(club);
						}}
					/>
				</div>
			);
		};

		return (
			<>
				<JoinClubConfirmModal
					isOpen={Boolean(joinClubToConfirm)}
					clubName={joinClubToConfirm?.name}
					isPending={isJoining}
					onClose={() => setJoinClubToConfirm(null)}
					onConfirm={() => {
						if (!joinClubToConfirm) return;
						handleJoinClub(joinClubToConfirm.id);
						setJoinClubToConfirm(null);
					}}
				/>
				<ClubsPageShell breadcrumbSuffix="PLANOS" onBack={onBack}>
					<div className="space-y-16">
						{isLoadingMyCompany || isLoadingClubsAvailable ? (
							<div className="flex justify-center py-20">
								<Loader2
									className="animate-spin text-brand-orange"
									size={40}
								/>
							</div>
						) : companyClubs.length > 0 ? (
							<section className="space-y-8">
								<div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:gap-4">
									<div className="flex size-12 shrink-0 items-center justify-center rounded-xl bg-brand-orange/10 text-brand-orange">
										<Building2 size={24} />
									</div>
									<div className="space-y-1">
										<p className="text-xs font-semibold uppercase tracking-wider text-brand-orange">
											Sua empresa
										</p>
										<h1 className="text-2xl font-bold tracking-tight text-zinc-900 md:text-3xl">
											{myCompany?.trade_name}
										</h1>
										<p className="text-sm text-zinc-500">
											Estes planos estão disponíveis para
											você através da sua empresa.
										</p>
										{activeCompanyPlan?.company_plan
											?.subsidy_per_employee &&
											Number(
												activeCompanyPlan.company_plan
													.subsidy_per_employee,
											) > 0 && (
												<p className="text-xs font-medium text-brand-orange">
													A empresa subsidia{' '}
													{new Intl.NumberFormat(
														'pt-BR',
														{
															style: 'currency',
															currency:
																activeCompanyPlan
																	.company_plan
																	.currency ||
																'BRL',
														},
													).format(
														Number(
															activeCompanyPlan
																.company_plan
																.subsidy_per_employee,
														),
													)}{' '}
													por colaborador.
												</p>
											)}
									</div>
								</div>
								<div
									className={cn(
										'grid grid-cols-1 items-stretch gap-6 px-2 py-6 pt-10 md:grid-cols-2 md:px-3',
										getPlansGridColsClass(
											companyClubs.length,
										),
									)}
								>
									{companyClubs.map((club, index) =>
										renderClubCard(
											club,
											index,
											club.render_type === 'HIGHLIGHTED',
										),
									)}
								</div>
								{shouldDisableJoinForEmployee && (
									<p className="mx-auto max-w-2xl text-center text-sm leading-relaxed text-zinc-500">
										No momento, a sua empresa ainda não
										ativou este benefício para
										colaboradores. Assim que ela regularizar
										o acesso, você poderá assinar um dos
										clubes acima.
									</p>
								)}
							</section>
						) : isInCompany ? (
							<section className="space-y-8">
								<div className="space-y-3 text-center">
									<h4 className="text-[11px] font-black uppercase tracking-[0.25em] text-brand-orange">
										Clubes da empresa
									</h4>
									<h2 className="text-3xl font-bold tracking-tight text-zinc-900 md:text-4xl">
										{myCompany?.trade_name}
									</h2>
									<p className="mx-auto max-w-2xl text-zinc-500">
										A sua empresa ainda não possui clubes
										ativos para você.
									</p>
								</div>

								<div className="flex flex-col items-center justify-center rounded-[2rem] border border-dashed border-zinc-200 bg-zinc-50/60 py-16 text-center">
									<div className="mb-4 rounded-full bg-white p-4 text-zinc-300 shadow-sm">
										<Building2 size={32} />
									</div>
									<h3 className="text-base font-bold text-zinc-900">
										Nenhum clube disponível no momento
									</h3>
									<p className="mx-auto mt-1 max-w-xs text-sm leading-relaxed text-zinc-400">
										Assim que sua empresa contratar um plano
										de clubes, as opções disponíveis para
										você vão aparecer aqui automaticamente.
									</p>
								</div>
							</section>
						) : (
							<section className="space-y-8">
								<div className="space-y-3 text-center">
									<h4 className="text-[11px] font-black uppercase tracking-[0.25em] text-brand-orange">
										Nossos Planos
									</h4>
									<h2 className="text-3xl font-bold tracking-tight text-zinc-900 md:text-5xl">
										A solução certa para o tamanho do seu
										desafio
									</h2>
									<p className="mx-auto max-w-2xl text-zinc-500">
										Escolha o cuidado que melhor se adapta
										às suas necessidades.
									</p>
								</div>
								<div className="h-4" />
								<div
									className={cn(
										'grid grid-cols-1 items-stretch gap-6 px-2 py-6 pt-10 md:grid-cols-2 md:px-3',
										getPlansGridColsClass(
											sortedB2CClubs.length,
										),
									)}
								>
									{sortedB2CClubs.map((club, index) =>
										renderClubCard(
											club,
											index,
											club.render_type === 'HIGHLIGHTED',
										),
									)}
								</div>
							</section>
						)}
					</div>
				</ClubsPageShell>
			</>
		);
	}

	// --- VIEW: DASHBOARD ---
	const userSubscription = myClub.subscription;
	const currentClub = userSubscription.club;

	const { processedBenefits, monthlySummary } = processClubBenefitsForUser(
		userSubscription,
		currentClub,
	);

	const consumableBenefits = processedBenefits.filter(
		(benefit) =>
			benefit.renewalTrigger === 'ALWAYS_ON_SUBSCRIPTION_PAYMENT' ||
			benefit.renewalTrigger === 'ALWAYS_ON_USE',
	);

	return (
		<ClubsPageShell breadcrumbSuffix="MEU PLANO" onBack={onBack}>
			<div className="space-y-12 text-slate-900">
				{/* Header */}
				<header className="flex flex-col justify-between gap-6 md:flex-row md:items-start">
					<div className="space-y-4">
						<div className="flex flex-wrap gap-2">
							<span className="rounded-md border border-brand-orange/25 bg-brand-orange/10 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider text-brand-orange">
								Assinatura{' '}
								{userSubscription.status === 'ACTIVE'
									? 'Ativa'
									: userSubscription.status}
							</span>
							<div
								className={cn(
									'flex items-center gap-1.5 rounded-md border px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider',
									myClub.paid
										? 'border-emerald-200 bg-emerald-50 text-emerald-800'
										: 'border-rose-200 bg-rose-50 text-rose-800',
								)}
							>
								<div
									className={cn(
										'h-1.5 w-1.5 rounded-full',
										myClub.paid
											? 'bg-emerald-500'
											: 'bg-rose-600',
									)}
								/>
								{myClub.paid
									? 'Em Dia'
									: 'Processando Pagamento'}
							</div>
						</div>
						<h1 className="text-4xl font-bold tracking-tight text-slate-900">
							{currentClub?.name || 'Seu Plano'}
						</h1>
						{currentClub?.slogan && (
							<p className="max-w-lg text-sm leading-relaxed text-slate-400">
								{currentClub.slogan}
							</p>
						)}
						<p className="max-w-lg leading-relaxed text-slate-600">
							{currentClub?.description ||
								'Sua jornada de saúde mental está sendo acompanhada pelos melhores especialistas da Plumaa.'}
						</p>
					</div>
					<button
						onClick={handleOpenPortal}
						className="self-start rounded-xl border border-slate-300 bg-white px-6 py-2.5 font-medium text-slate-700 shadow-sm transition-colors hover:bg-slate-50"
					>
						Gerenciar
					</button>
				</header>

				{/* Grid principal: resumo + seus benefícios à esquerda, lista lateral mais fina à direita */}
				<div className="grid grid-cols-1 gap-6 lg:grid-cols-[minmax(0,2.4fr)_minmax(260px,0.8fr)] lg:items-start">
					{/* Coluna esquerda: Resumo + Seus benefícios */}
					<div className="space-y-6">
						<ClubMonthlySummaryCard summary={monthlySummary} />

						<ClubConsumableBenefitsSection
							benefits={consumableBenefits}
							onUseBenefit={handleUseBenefit}
							onRedeemBenefit={(benefit) => {
								openModal({
									title: null,
									content: (
										<BenefitRedeemModal
											title="Resgate de benefício"
											description={
												benefit.description || null
											}
											origin={
												(benefit.serviceOrigin ??
													'INTERNAL') as Services.Origin
											}
										/>
									),
								});
							}}
						/>
					</div>

					{/* Coluna direita: Lista de benefícios + Situação Financeira */}
					<div className="flex flex-col gap-6">
						<div className="flex flex-col rounded-[32px] border border-slate-100 bg-white p-7 shadow-sm">
							<div className="space-y-6">
								<div className="flex items-baseline gap-1 whitespace-nowrap">
									<h2 className="text-base font-semibold text-[#111827] md:text-lg md:font-bold">
										Lista de benefícios
									</h2>
									<span className="text-xs font-medium text-[#9CA3AF] md:text-sm">
										({allBenefitsList.length})
									</span>
								</div>

								{allBenefitsList.length > 0 ? (
									<ul className="space-y-5">
										{allBenefitsList.map((item: any) => (
											<li
												key={item.id}
												className="flex items-start gap-3"
											>
												<div className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-brand-orange/10 text-brand-orange">
													<Check
														size={12}
														strokeWidth={3}
													/>
												</div>
												<div className="min-w-0 space-y-0.5">
													<p className="text-sm font-semibold leading-tight text-[#374151]">
														{capitalizeFirst(
															item.title,
														)}
													</p>
													{item.description &&
														item.description !==
															item.title && (
															<p className="text-[10px] font-bold uppercase tracking-wider text-[#9CA3AF]">
																{capitalizeFirst(
																	item.description,
																)}
															</p>
														)}
												</div>
											</li>
										))}
									</ul>
								) : (
									<p className="text-sm text-slate-400">
										Nenhum benefício vinculado a este plano.
									</p>
								)}
							</div>
						</div>

						{/* Assinatura (card separado, abaixo da Lista de benefícios) */}
						<div className="flex flex-col items-center rounded-[32px] border border-slate-100 bg-white p-7 text-center shadow-sm">
							<div
								className={cn(
									'mb-4 flex h-12 w-12 shrink-0 items-center justify-center rounded-full text-white',
									myClub.paid
										? 'bg-[#10B981]'
										: 'bg-rose-600',
								)}
							>
								{myClub.paid ? (
									<Check size={24} strokeWidth={4} />
								) : (
									<X size={24} strokeWidth={4} />
								)}
							</div>
							<h2 className="mb-2 text-base font-bold text-[#111827] md:text-lg">
								Assinatura
							</h2>
							<p className="mb-4 text-sm font-medium leading-relaxed text-[#6B7280]">
								{myClub.paid
									? 'Tudo certo! Sua última mensalidade está confirmada.'
									: 'Estamos processando ou aguardando seu pagamento.'}
							</p>
							<button
								onClick={handleOpenPortal}
								className="group block w-full rounded-xl border border-brand-orange/30 bg-brand-orange/10 px-5 py-2.5 text-center text-sm font-bold text-brand-orange transition-all hover:bg-brand-orange/15"
							>
								<span className="inline">
									Ver histórico de pagamento{' '}
									<ArrowRight
										size={16}
										className="inline-block align-middle transition-transform group-hover:translate-x-1"
									/>
								</span>
							</button>
						</div>
					</div>
				</div>
			</div>
		</ClubsPageShell>
	);
};

export { UserClubs };
